import { useEffect, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge, Button, Input, Switch, Textarea } from '@/components/ui';
import { Icon } from '@/lib/icons';
import {
  getChatAdmin, saveChatAdmin, rotateChatToken, getChatCorpus, addChatKnowledge, updateChatKnowledge, deleteChatKnowledge,
  startChatTest, sendChatTest, getChatSessions, getChatSession, getChatGaps, testChatBooking, answerChatGap,
  type ChatConfig, type ChatKnowledge, type ChatSessionRow,
} from '@/data/client';
import type { AdminClient, DirtyReporter } from './types';
import { apiErrMsg } from './util';

/* conversationIQ for one client: the rules the bot follows, where leads go,
   the FAQ it answers from, what it knows (the compiled corpus), a test console
   that runs the real pipeline without creating leads, and the conversations it
   has had. */
export function ConversationForm({ client, onDirtyChange }: { client: AdminClient; onDirtyChange?: DirtyReporter }) {
  const qc = useQueryClient();
  const key = ['admin', 'chat', client.orgId];
  const { data, isLoading, error } = useQuery({ queryKey: key, queryFn: () => getChatAdmin(client.orgId) });
  const [cfg, setCfg] = useState<ChatConfig | null>(null);
  const [email, setEmail] = useState('');
  const [saved, setSaved] = useState('');
  const [saving, setSaving] = useState(false);
  const [saveMsg, setSaveMsg] = useState<string | null>(null);
  const [section, setSection] = useState<'settings' | 'knowledge' | 'corpus' | 'test' | 'sessions'>('settings');

  useEffect(() => {
    if (!data || cfg) return;
    setCfg(data.config); setEmail(data.salesLeadEmail);
    setSaved(JSON.stringify({ c: data.config, e: data.salesLeadEmail }));
  }, [data, cfg]);
  const dirty = !!cfg && JSON.stringify({ c: cfg, e: email }) !== saved;
  useEffect(() => { onDirtyChange?.('conversation', dirty); }, [dirty, onDirtyChange]);

  const save = async () => {
    if (!cfg) return;
    setSaving(true); setSaveMsg(null);
    try {
      const r = await saveChatAdmin(client.orgId, { config: cfg, salesLeadEmail: email });
      setCfg(r.config); setSaved(JSON.stringify({ c: r.config, e: email })); setSaveMsg('Saved.');
      qc.invalidateQueries({ queryKey: key });
    } catch (e) { setSaveMsg(apiErrMsg(e, 'Could not save.')); }
    finally { setSaving(false); }
  };

  if (isLoading || !cfg) return <div className="admin-form"><div className="admin-form__body"><p className="dash-muted">{error ? apiErrMsg(error, 'Could not load.') : 'Loading…'}</p></div></div>;
  const set = <K extends keyof ChatConfig>(k: K, v: ChatConfig[K]) => setCfg({ ...cfg, [k]: v });
  const stats = data?.stats30d;

  return (
    <div className="admin-form">
      <div className="admin-form__body">
        <div className="admin-chat__head">
          <div className="admin-chat__status">
            {cfg.enabled ? <Badge variant="success" dot>Live on the site</Badge> : <Badge variant="neutral">Off</Badge>}
            {stats && <span className="dash-muted">Last 30 days: {stats.sessions} conversations · {stats.leads} leads · {stats.unanswered} unanswered questions</span>}
          </div>
          <div className="admin-chat__tabs">
            {(['settings', 'knowledge', 'corpus', 'test', 'sessions'] as const).map((s) => (
              <button key={s} type="button" className={`admin-chat__tab ${section === s ? 'is-active' : ''}`} onClick={() => setSection(s)}>
                {s === 'settings' ? 'Settings' : s === 'knowledge' ? `FAQ (${data?.knowledge.length ?? 0})` : s === 'corpus' ? 'What it knows' : s === 'test' ? 'Test console' : 'Conversations'}
              </button>
            ))}
          </div>
        </div>

        {section === 'settings' && (
          <>
            <section className="admin-sect">
              <h4 className="admin-sect__title">Persona</h4>
              <div className="admin-prof">
                <Switch label="Chat is live on the website" checked={cfg.enabled} onChange={(e) => set('enabled', e.target.checked)} />
                <Input label="Persona name" hint="Shown in the widget; the greeting says it is an AI assistant." value={cfg.personaName} onChange={(e) => set('personaName', e.target.value)} />
                <label className="tr-field" style={{ gridColumn: '1 / -1' }}>
                  <span className="tr-field__label">Greeting</span>
                  <Textarea rows={2} value={cfg.greeting} onChange={(e) => set('greeting', e.target.value)} />
                  <span className="admin-hint">{'{persona}'} and {'{business}'} are replaced. Preview: <em>{cfg.greeting.replace(/\{persona\}/g, cfg.personaName).replace(/\{business\}/g, client.name)}</em></span>
                </label>
                <label className="tr-field">
                  <span className="tr-field__label">Tone</span>
                  <select className="tr-select" value={cfg.tone} onChange={(e) => set('tone', e.target.value as ChatConfig['tone'])}>
                    <option value="friendly">Friendly</option><option value="professional">Professional</option><option value="plain">Plain</option>
                  </select>
                </label>
                <Input label="Accent colour" value={cfg.accent} onChange={(e) => set('accent', e.target.value)} hint="Hex, e.g. #00abd8" />
                <label className="tr-field">
                  <span className="tr-field__label">Launcher</span>
                  <select className="tr-select" value={cfg.launcher || 'bubble'} onChange={(e) => set('launcher', e.target.value as ChatConfig['launcher'])}>
                    <option value="bubble">Chat bubble (round icon)</option>
                    <option value="pill">&ldquo;Ask me anything&rdquo; pill</option>
                  </select>
                  <span className="admin-hint">The pill is white with the text and a sparkle in the accent colour; it opens the same chat. Takes effect on the site within five minutes.</span>
                </label>
                {(cfg.launcher || 'bubble') === 'pill' && (
                  <Input label="Launcher text" value={cfg.launcherLabel || ''} onChange={(e) => set('launcherLabel', e.target.value)} placeholder="Ask me anything…" hint="Short — it is a button. Up to 40 characters." />
                )}
              </div>
            </section>
            <section className="admin-sect">
              <h4 className="admin-sect__title">Leads</h4>
              <div className="admin-prof">
                <Input label="Sales lead email" hint="Every captured lead is emailed here and lands in leadIQ. Falls back to the account manager if empty." value={email} onChange={(e) => setEmail(e.target.value)} placeholder="sales@client.com" />
                <label className="tr-field">
                  <span className="tr-field__label">Handoff message</span>
                  <Textarea rows={2} value={cfg.handoffMessage} onChange={(e) => set('handoffMessage', e.target.value)} />
                  <span className="admin-hint">Said once a name and a phone or email are captured, and when the bot cannot help.</span>
                </label>
                <Switch label="Show the business phone number when asked" checked={cfg.showPhone} onChange={(e) => set('showPhone', e.target.checked)} />
              </div>
            </section>
            <section className="admin-sect">
              <h4 className="admin-sect__title">Rules</h4>
              <div className="admin-prof">
                <Switch label="Allowed to discuss pricing" checked={cfg.allowPricing} onChange={(e) => set('allowPricing', e.target.checked)} />
                <p className="admin-hint" style={{ margin: 0 }}>Off by default. When off, any dollar figure the model produces is replaced with the handoff line before the visitor sees it. Turn on only when the FAQ or site states prices.</p>
                <Input label="Other topics to decline" hint="Comma-separated, e.g. warranty claims, hiring" value={cfg.forbiddenTopics.join(', ')} onChange={(e) => set('forbiddenTopics', e.target.value.split(',').map((x) => x.trim()).filter(Boolean))} />
                <label className="tr-field" style={{ gridColumn: '1 / -1' }}>
                  <span className="tr-field__label">Custom rules for this business</span>
                  <Textarea rows={5} value={cfg.customRules} onChange={(e) => set('customRules', e.target.value)} placeholder={'One rule per line, e.g.\nNever promise same-day service.\nAlways mention the free estimate.\nRefer commercial jobs to the office.'} />
                </label>
                <Input label="Extra allowed origins" hint="Besides the website in the profile. Comma-separated, e.g. https://staging.client.com" value={cfg.allowedOrigins.join(', ')} onChange={(e) => set('allowedOrigins', e.target.value.split(',').map((x) => x.trim()).filter(Boolean))} />
              </div>
            </section>
            <section className="admin-sect">
              <h4 className="admin-sect__title">Appointment booking</h4>
              <div className="admin-prof">
                <label className="tr-field">
                  <span className="tr-field__label">Calendar</span>
                  <select className="tr-select" value={cfg.booking.kind} onChange={(e) => set('booking', { ...cfg.booking, kind: e.target.value as ChatConfig['booking']['kind'] })}>
                    <option value="none">None — record preferred times on the lead</option>
                    <option value="google_calendar">Google Calendar</option>
                  </select>
                </label>
                {cfg.booking.kind === 'google_calendar' && (
                  <>
                    <Input label="Calendar id (email)" hint="A Triton Workspace user (impersonated) or any Google calendar shared with the service account." value={cfg.booking.calendarId} onChange={(e) => set('booking', { ...cfg.booking, calendarId: e.target.value })} />
                    <Switch label="Triton Workspace user (impersonate)" checked={cfg.booking.impersonate} onChange={(e) => set('booking', { ...cfg.booking, impersonate: e.target.checked })} />
                    <Input label="Event title" value={cfg.booking.title} onChange={(e) => set('booking', { ...cfg.booking, title: e.target.value })} />
                    <label className="tr-field"><span className="tr-field__label">Slot length</span>
                      <select className="tr-select" value={cfg.booking.slotMinutes} onChange={(e) => set('booking', { ...cfg.booking, slotMinutes: Number(e.target.value) })}>{[30, 45, 60, 90, 120].map((m) => <option key={m} value={m}>{m} minutes</option>)}</select>
                    </label>
                    <Input label="Hours (start–end, 24h)" value={`${cfg.booking.startHour}-${cfg.booking.endHour}`} onChange={(e) => { const m = /^(\d{1,2})\s*-\s*(\d{1,2})$/.exec(e.target.value.trim()); if (m) set('booking', { ...cfg.booking, startHour: Number(m[1]), endHour: Number(m[2]) }); }} hint="e.g. 9-17" />
                    <Input label="Days (ISO weekdays)" value={cfg.booking.days.join(',')} onChange={(e) => set('booking', { ...cfg.booking, days: e.target.value.split(',').map((x) => Number(x.trim())).filter((x) => x >= 1 && x <= 7) })} hint="1 = Monday … 7 = Sunday, e.g. 1,2,3,4,5" />
                    <Input label="Time zone" value={cfg.booking.timezone} onChange={(e) => set('booking', { ...cfg.booking, timezone: e.target.value })} hint="e.g. America/Chicago" />
                    <div style={{ gridColumn: '1 / -1' }}><BookingTest orgId={client.orgId} dirty={dirty} /></div>
                  </>
                )}
                <p className="admin-hint" style={{ gridColumn: '1 / -1', margin: 0 }}>With a calendar, the bot offers open times and books the visit once it has a name and a phone or email. ServiceTitan and the SMS handoff will appear here as further options.</p>
              </div>
            </section>
            <section className="admin-sect">
              <h4 className="admin-sect__title">Install</h4>
              <div className="admin-prof">
                <p className="admin-hint" style={{ gridColumn: '1 / -1', margin: 0 }}>Add this once to the site's base layout, before <code>&lt;/body&gt;</code>. One deferred 3 KB script; the chat itself loads in an iframe after the visitor clicks. Full Astro notes in <code>docs/conversationiq-astro.md</code>.</p>
                <textarea className="admin-chat__corpus" style={{ gridColumn: '1 / -1', minHeight: 72 }} readOnly value={data?.token ? `<script is:inline defer src="${window.location.origin}/conversationiq/v1/loader.js" data-token="${data.token}"></script>` : 'Save once to mint the widget token.'} onFocus={(e) => e.currentTarget.select()} />
                <div style={{ gridColumn: '1 / -1' }}>
                  <Button size="sm" variant="ghost" onClick={async () => { if (!window.confirm('Rotate the token? Sites using the old one stop working until updated.')) return; await rotateChatToken(client.orgId); qc.invalidateQueries({ queryKey: key }); }}>Rotate token</Button>
                </div>
              </div>
            </section>
            <div className="admin-form__footer" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <Button disabled={!dirty || saving} onClick={save}>{saving ? 'Saving…' : 'Save conversationIQ'}</Button>
              {saveMsg && <span className="dash-muted" style={{ fontSize: 'var(--text-xs)' }}>{saveMsg}</span>}
            </div>
          </>
        )}

        {section === 'knowledge' && <KnowledgeEditor orgId={client.orgId} items={data?.knowledge ?? []} onChanged={() => qc.invalidateQueries({ queryKey: key })} />}
        {section === 'corpus' && <CorpusPanel orgId={client.orgId} />}
        {section === 'test' && <TestConsole orgId={client.orgId} persona={cfg.personaName} />}
        {section === 'sessions' && <SessionsPanel orgId={client.orgId} />}
      </div>
    </div>
  );
}

function KnowledgeEditor({ orgId, items, onChanged }: { orgId: string; items: ChatKnowledge[]; onChanged: () => void }) {
  const [q, setQ] = useState(''); const [a, setA] = useState('');
  const [busy, setBusy] = useState(false); const [msg, setMsg] = useState<string | null>(null);
  const [editing, setEditing] = useState<ChatKnowledge | null>(null);
  const add = async () => {
    setBusy(true); setMsg(null);
    try { await addChatKnowledge(orgId, { question: q, answer: a }); setQ(''); setA(''); onChanged(); }
    catch (e) { setMsg(apiErrMsg(e, 'Could not add.')); } finally { setBusy(false); }
  };
  return (
    <section className="admin-sect">
      <h4 className="admin-sect__title">FAQ the bot answers from</h4>
      <p className="admin-hint">Questions visitors ask and the answer the business wants given. These are the most reliable source the bot has; unanswered questions from real conversations show under Conversations so you can add them here.</p>
      <div className="admin-prof">
        <Input label="Question" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Do you offer financing?" />
        <label className="tr-field" style={{ gridColumn: '1 / -1' }}><span className="tr-field__label">Answer</span><Textarea rows={3} value={a} onChange={(e) => setA(e.target.value)} /></label>
        <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 10, alignItems: 'center' }}>
          <Button size="sm" disabled={busy || !q.trim() || !a.trim()} onClick={add} iconLeft={<Icon name="plus" size={14} />}>Add</Button>
          {msg && <span className="admin-modal__err">{msg}</span>}
        </div>
      </div>
      {items.length > 0 && (
        <div className="admin-chat__faq">
          {items.map((k) => (
            <div key={k.id} className={`admin-chat__faqitem ${k.enabled ? '' : 'is-off'}`}>
              {editing?.id === k.id ? (
                <div className="admin-prof" style={{ width: '100%' }}>
                  <Input label="Question" value={editing.question} onChange={(e) => setEditing({ ...editing, question: e.target.value })} />
                  <label className="tr-field" style={{ gridColumn: '1 / -1' }}><span className="tr-field__label">Answer</span><Textarea rows={3} value={editing.answer} onChange={(e) => setEditing({ ...editing, answer: e.target.value })} /></label>
                  <div style={{ gridColumn: '1 / -1', display: 'flex', gap: 8 }}>
                    <Button size="sm" onClick={async () => { await updateChatKnowledge(orgId, k.id, { question: editing.question, answer: editing.answer, enabled: editing.enabled }); setEditing(null); onChanged(); }}>Save</Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>Cancel</Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="admin-chat__faqtext"><strong>{k.question}</strong><span>{k.answer}</span></div>
                  <div className="admin-chat__faqactions">
                    <button type="button" onClick={() => setEditing(k)} title="Edit"><Icon name="edit" size={14} /></button>
                    <button type="button" onClick={async () => { await updateChatKnowledge(orgId, k.id, { question: k.question, answer: k.answer, enabled: !k.enabled }); onChanged(); }} title={k.enabled ? 'Disable' : 'Enable'}><Icon name={k.enabled ? 'eye' : 'eye-off'} size={14} /></button>
                    <button type="button" onClick={async () => { if (window.confirm('Delete this entry?')) { await deleteChatKnowledge(orgId, k.id); onChanged(); } }} title="Delete"><Icon name="trash" size={14} /></button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function CorpusPanel({ orgId }: { orgId: string }) {
  const [rebuild, setRebuild] = useState(0);
  const { data, isLoading, refetch, isFetching } = useQuery({ queryKey: ['admin', 'chat-corpus', orgId, rebuild], queryFn: () => getChatCorpus(orgId, rebuild > 0) });
  const kinds = data ? data.sources.reduce<Record<string, number>>((m, s) => ({ ...m, [s.kind]: (m[s.kind] ?? 0) + 1 }), {}) : {};
  return (
    <section className="admin-sect">
      <h4 className="admin-sect__title">What the bot knows</h4>
      <p className="admin-hint">Compiled from the company profile, the FAQ, published articles and the crawled website pages in the vault. Interview transcripts and uploaded documents are never included. Rebuilt automatically when any of those change.</p>
      {isLoading ? <p className="dash-muted">Compiling…</p> : data && (
        <>
          <div style={{ display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap', margin: '8px 0' }}>
            <span className="dash-muted" style={{ fontSize: 'var(--text-sm)' }}>{data.sources.length} sources · about {Math.round(data.tokens / 1000)}k tokens · {Object.entries(kinds).map(([k, n]) => `${n} ${k}${n === 1 ? '' : 's'}`).join(', ')} · built {data.builtAt.slice(0, 16).replace('T', ' ')}</span>
            <Button size="sm" variant="outline" disabled={isFetching} onClick={() => { setRebuild((n) => n + 1); void refetch(); }}>{isFetching ? 'Rebuilding…' : 'Rebuild now'}</Button>
          </div>
          <textarea className="admin-chat__corpus" readOnly value={data.text} />
        </>
      )}
    </section>
  );
}

function TestConsole({ orgId, persona }: { orgId: string; persona: string }) {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [msgs, setMsgs] = useState<{ role: 'user' | 'assistant'; text: string; note?: string }[]>([]);
  const [draft, setDraft] = useState('');
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState('');
  const endRef = useRef<HTMLDivElement>(null);
  useEffect(() => { endRef.current?.scrollIntoView({ block: 'end' }); }, [msgs.length, status]);
  const start = async () => { const r = await startChatTest(orgId); setSessionId(r.sessionId); setMsgs([{ role: 'assistant', text: r.greeting }]); };
  const send = async () => {
    const text = draft.trim(); if (!text || busy) return;
    let sid = sessionId;
    if (!sid) { const r = await startChatTest(orgId); sid = r.sessionId; setSessionId(sid); setMsgs([{ role: 'assistant', text: r.greeting }]); }
    setDraft(''); setBusy(true); setStatus('Thinking…');
    setMsgs((m) => [...m, { role: 'user', text }]);
    try {
      await sendChatTest(orgId, sid, text, (ev, d) => {
        if (ev === 'status') setStatus((d as { text: string }).text);
        if (ev === 'answer') {
          const a = d as { text: string; citations: { title: string }[]; capture?: Record<string, string>; wouldCreateLead?: boolean; unanswered: string[]; pricingBlocked: boolean };
          const notes = [
            a.citations.length ? `Sources: ${a.citations.map((c) => c.title).join(' · ')}` : '',
            a.capture && Object.keys(a.capture).length ? `Captured: ${Object.entries(a.capture).map(([k, v]) => `${k}=${v}`).join(', ')}${a.wouldCreateLead ? ' → would create a lead + email' : ''}` : '',
            a.unanswered.length ? `Unanswered: ${a.unanswered.join(' / ')}` : '', a.pricingBlocked ? 'Pricing backstop replaced the answer' : '',
          ].filter(Boolean).join(' · ');
          setMsgs((m) => [...m, { role: 'assistant', text: a.text, note: notes }]);
        }
      });
    } catch (e) { setMsgs((m) => [...m, { role: 'assistant', text: apiErrMsg(e, 'The test failed.') }]); }
    finally { setBusy(false); setStatus(''); }
  };
  return (
    <section className="admin-sect">
      <h4 className="admin-sect__title">Test console</h4>
      <p className="admin-hint">The real pipeline on a test session: same corpus, rules and model. No lead is created and nothing is emailed; the notes under each answer show what would have happened.</p>
      <div className="admin-chat__console">
        <div className="admin-chat__log">
          {msgs.length === 0 && <button type="button" className="asst-drawer__link" onClick={start}>Start a conversation as a visitor</button>}
          {msgs.map((m, i) => (
            <div key={i} className={`admin-chat__msg admin-chat__msg--${m.role}`}>
              <span className="admin-chat__who">{m.role === 'user' ? 'Visitor' : persona}</span>
              <span className="admin-chat__text">{m.text}</span>
              {m.note && <span className="admin-chat__note">{m.note}</span>}
            </div>
          ))}
          {status && <div className="dash-muted" style={{ fontSize: 'var(--text-xs)' }}>{status}</div>}
          <div ref={endRef} />
        </div>
        <form className="admin-chat__composer" onSubmit={(e) => { e.preventDefault(); void send(); }}>
          <input className="tr-input" value={draft} onChange={(e) => setDraft(e.target.value)} placeholder="Type as a visitor…" disabled={busy} />
          <Button type="submit" size="sm" disabled={busy || !draft.trim()}>Send</Button>
          {sessionId && <Button type="button" size="sm" variant="ghost" onClick={() => { setSessionId(null); setMsgs([]); }}>Reset</Button>}
        </form>
      </div>
    </section>
  );
}

function SessionsPanel({ orgId }: { orgId: string }) {
  const qcGaps = useQueryClient();
  const { data } = useQuery({ queryKey: ['admin', 'chat-sessions', orgId], queryFn: () => getChatSessions(orgId) });
  const { data: gaps } = useQuery({ queryKey: ['admin', 'chat-gaps', orgId], queryFn: () => getChatGaps(orgId) });
  const [open, setOpen] = useState<string | null>(null);
  const { data: detail } = useQuery({ queryKey: ['admin', 'chat-session', orgId, open], queryFn: () => getChatSession(orgId, open!), enabled: !!open });
  const rows: ChatSessionRow[] = data?.sessions ?? [];
  return (
    <>
      {gaps && gaps.gaps.length > 0 && (
        <section className="admin-sect">
          <h4 className="admin-sect__title">Questions the bot could not answer</h4>
          <p className="admin-hint">Answer one and it becomes an FAQ entry; the corpus rebuilds on the next request.</p>
          <ul className="admin-chat__gaps">{gaps.gaps.map((g) => <AdminGapRow key={g.question} orgId={orgId} question={g.question} n={g.n} onAnswered={() => { qcGaps.invalidateQueries({ queryKey: ['admin', 'chat-gaps', orgId] }); qcGaps.invalidateQueries({ queryKey: ['admin', 'chat', orgId] }); }} />)}</ul>
        </section>
      )}
      <section className="admin-sect">
        <h4 className="admin-sect__title">Conversations</h4>
        {rows.length === 0 ? <p className="dash-muted">None yet.</p> : (
          <div className="asst-admin">
            {rows.map((s) => (
              <div key={s.id} className="asst-admin__thread">
                <button type="button" className="asst-admin__head" onClick={() => setOpen(open === s.id ? null : s.id)}>
                  <span className="asst-admin__title">{s.summary || (s.is_test ? 'Test session' : 'Conversation in progress')}</span>
                  <span className="dash-muted">{s.started_at.slice(0, 16)} · {Math.floor(s.message_count / 2)} Q{s.intent ? ` · ${s.intent}` : ''} · {s.source}</span>
                  {s.status === 'lead' && <Badge variant="success" dot>lead{s.capture.name ? `: ${s.capture.name}` : ''}</Badge>}
                  {s.is_test && <Badge variant="neutral">test</Badge>}
                  {s.unanswered > 0 && <Badge variant="warning">{s.unanswered} unanswered</Badge>}
                </button>
                {open === s.id && detail && (
                  <div className="asst-admin__msgs">
                    {detail.messages.length === 0 && <p className="dash-muted">Transcript no longer kept (30-day retention for non-lead conversations).</p>}
                    {detail.messages.map((m) => <div key={m.id} className={`asst-admin__msg asst-admin__msg--${m.role}`}><div className="asst-admin__text">{m.text}</div></div>)}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </>
  );
}

function BookingTest({ orgId, dirty }: { orgId: string; dirty: boolean }) {
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
      <Button size="sm" variant="outline" disabled={busy || dirty} title={dirty ? 'Save first' : undefined} onClick={async () => {
        setBusy(true); setMsg(null);
        try { const r = await testChatBooking(orgId); setMsg(r.ok ? `Calendar reachable — ${r.slots} open slots this week${r.sample?.length ? ` (${r.sample.join(', ')}…)` : ''}.` : r.error || 'Failed.'); }
        catch (e) { setMsg(apiErrMsg(e, 'Test failed.')); } finally { setBusy(false); }
      }}>{busy ? 'Checking…' : 'Test calendar'}</Button>
      {dirty && <span className="dash-muted" style={{ fontSize: 'var(--text-xs)' }}>Save the settings, then test.</span>}
      {msg && <span className="dash-muted" style={{ fontSize: 'var(--text-xs)' }}>{msg}</span>}
    </div>
  );
}

function AdminGapRow({ orgId, question, n, onAnswered }: { orgId: string; question: string; n: number; onAnswered: () => void }) {
  const [open, setOpen] = useState(false);
  const [answer, setAnswer] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  return (
    <li style={{ flexDirection: 'column', alignItems: 'stretch', gap: 8 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
        <span>{question}</span>
        <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}><span className="dash-muted">×{n}</span>{!open && <Button size="sm" variant="outline" onClick={() => setOpen(true)}>Answer</Button>}</span>
      </div>
      {open && (
        <>
          <Textarea rows={3} value={answer} onChange={(e) => setAnswer(e.target.value)} placeholder="The answer, as the business would say it." />
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Button size="sm" disabled={busy || !answer.trim()} onClick={async () => { setBusy(true); setMsg(null); try { await answerChatGap(orgId, { question, answer }); setOpen(false); onAnswered(); } catch (e) { setMsg(apiErrMsg(e, 'Could not save.')); } finally { setBusy(false); } }}>{busy ? 'Saving…' : 'Save to FAQ'}</Button>
            <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
            {msg && <span className="admin-modal__err">{msg}</span>}
          </div>
        </>
      )}
    </li>
  );
}
